import { useEffect, useState } from "react";
import { accountJobOutcomes } from "../../Api/accountQueries";
import type { BulkJob, OutcomePage } from "../../Types/accountQuery";
import { AccountSearchDialog } from "./AccountSearchDialog";
import {
  accountEditSummary,
  accountEditPending,
  accountEditExceptions,
} from "./accountEditStatus";
import "../../Css/Account/AccountBulkEdit.css";

export function AccountEditResults({
  job,
  onClose,
  delayed = false,
  refresh,
}: {
  job: BulkJob;
  onClose: () => void;
  delayed?: boolean;
  refresh?: () => void;
}) {
  const exceptions = accountEditExceptions(job);
  const [view, setView] = useState<"all" | "exceptions">(
    exceptions ? "exceptions" : "all",
  );
  const [offset, setOffset] = useState(0);
  const [result, setResult] = useState<{ key: string; page: OutcomePage }>();
  const [failure, setFailure] = useState<{ key: string; message: string }>();
  const [retry, setRetry] = useState(0);
  const key = JSON.stringify([job.id, offset === 0 ? job.counts : null, view, offset, retry]);
  const error = failure?.key === key ? failure.message : "";
  const page = result?.key === key ? result.page : undefined;
  useEffect(() => {
    const controller = new AbortController();
    accountJobOutcomes(job.id, offset, controller.signal, view)
      .then((page) => {
        if (!controller.signal.aborted) setResult({ key, page });
      })
      .catch((error: Error) => {
        if (!controller.signal.aborted)
          setFailure({ key, message: error.message });
      });
    return () => controller.abort();
  }, [job.id, offset, view, key]);
  return (
    <AccountSearchDialog
      title="Account edit results"
      className="accountBulkEdit accountEditResults"
      onClose={onClose}
      footer={
        <button type="button" onClick={onClose}>
          Close
        </button>
      }
    >
      <p className="accountEditResults_summary">{accountEditSummary(job)}</p>
      {delayed && <p role="status">Live updates are delayed. <button type="button" onClick={refresh}>Refresh</button></p>}
      <p>
        {job.client_name ?? "Selected client"} ·{" "}
        {new Date(job.created_at).toLocaleString()}
      </p>
      <div className="accountEditResults_filters" aria-label="Result filters">
        <button
          type="button"
          aria-pressed={view === "exceptions"}
          onClick={() => {
            setView("exceptions");
            setOffset(0);
          }}
        >
          Skipped and failed ({exceptions.toLocaleString()})
        </button>
        <button
          type="button"
          aria-pressed={view === "all"}
          onClick={() => {
            setView("all");
            setOffset(0);
          }}
        >
          All accounts ({job.count.toLocaleString()})
        </button>
      </div>
      {view === "exceptions" && (
        <p className="accountBulkEdit_hint">
          Skipped accounts were not changed. Reasons distinguish access or
          eligibility changes from failures. Successful accounts do not need to
          be submitted again.
        </p>
      )}
      {error ? (
        <p role="alert">
          {error}{" "}
          <button type="button" onClick={() => setRetry((value) => value + 1)}>
            Retry loading results
          </button>
        </p>
      ) : !page ? (
        <p role="status">Loading results…</p>
      ) : (
        <>
          {!page.items.length ? (
            <p>
              {accountEditPending(job)
                ? "No exceptions recorded yet. This edit is still running."
                : "No accounts in this result view."}
            </p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Outcome</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {page.items.map((item) => (
                  <tr key={item.account_id}>
                    <td>
                      {item.account_name ?? "Account no longer available"}
                    </td>
                    <td>
                      {item.status === "succeeded"
                        ? "Saved"
                        : item.status.replaceAll("_", " ")}
                    </td>
                    <td>
                      {item.error === "CrmMatchDismissed"
                        ? "Previous match review was dismissed. Retry linking this account to review fresh matches."
                        : (item.error ??
                          (item.status === "skipped"
                            ? "No longer accessible or eligible for this change."
                            : "—"))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p>
            {page.total.toLocaleString()}{" "}
            {view === "exceptions" ? "skipped or failed accounts" : "accounts"}
          </p>
        </>
      )}
      <div className="accountBulkEdit_pagination">
        <button
          type="button"
          disabled={!offset || !page}
          onClick={() => setOffset((value) => Math.max(0, value - 25))}
        >
          Previous results
        </button>
        <button
          type="button"
          disabled={!page?.has_more}
          onClick={() => setOffset((value) => value + 25)}
        >
          Next results
        </button>
      </div>
    </AccountSearchDialog>
  );
}
